import { useMemo } from 'react';
import { useTypesenseFacets } from './useTypesenseFacets';
import { TYPESENSE_FIELDS } from '~/utils/shipmentSearch/typesense-constants';
import type { TypesenseFacetCount } from '~/types/typesense-search';

interface FulfillmentContextActivityParams {
  startDate: string;
  endDate: string;
  enabled?: boolean;
}

/**
 * Hook to fetch fulfillment context activity counts using Typesense facets.
 * Wraps useTypesenseFacets and extracts counts from fulfillment_context_id_facet.
 * Returns a map of contextId -> shipment count for the given date range.
 */
export const useFulfillmentContextActivity = ({
  startDate,
  endDate,
  enabled = true,
}: FulfillmentContextActivityParams) => {
  const { overallFacetCounts, isLoading, error } = useTypesenseFacets(
    {
      startDate,
      endDate,
      facetFilters: { test_mode_facet: 'false' },
    },
    enabled,
  );

  const data = useMemo(() => {
    const contextFacet = overallFacetCounts?.find(
      (facet: TypesenseFacetCount) => facet.field_name === TYPESENSE_FIELDS.FULFILLMENT_CONTEXT_ID_FACET,
    );
    if (!contextFacet?.counts) {
      return {};
    }
    return contextFacet.counts.reduce<Record<string, number>>((map, item) => {
      if (item.value) {
        map[item.value] = item.count;
      }
      return map;
    }, {});
  }, [overallFacetCounts]);

  return { data, isLoading, error };
};
